import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { BlogCard } from "@/components/BlogCard";
import { Newsletter } from "@/components/Newsletter";
import { AdSlot, AdDisclaimer } from "@/components/AdSlot";
import { Clock } from "lucide-react";

export default function BlogHome() {
  const [posts, setPosts] = useState([]);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Xurray Journal — Stories on African Food, Culture & Style";
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get(`/blog/posts${active !== "all" ? `?category=${encodeURIComponent(active)}` : ""}`)
      .then((r) => setPosts(r.data.posts))
      .finally(() => setLoading(false));
  }, [active]);

  useEffect(() => {
    api.get("/blog/categories").then((r) => setCats(r.data.categories));
  }, []);

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <Layout hideAd>
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero */}
        <header className="text-center mb-8">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Xurray Journal</p>
          <h1 className="font-display font-bold text-4xl sm:text-5xl mt-2">Stories from the heart of Africa</h1>
          <p className="text-base text-muted-foreground mt-3 max-w-2xl mx-auto">
            Recipes, culture, beauty, fashion and smart marketplace tips — original writing celebrating authentic African products across Europe.
          </p>
        </header>

        {/* Header banner ad */}
        <AdSlot placement="header" />

        {/* Featured */}
        {featured && (
          <section aria-label="Featured article" className="mb-10">
            <Link to={`/blog/${featured.slug}`} className="group grid md:grid-cols-2 gap-6 items-center bg-card rounded-3xl border border-border overflow-hidden hover:shadow-xl transition-shadow" data-testid="featured-post">
              <div className="aspect-[16/10] md:h-full overflow-hidden bg-muted">
                <img src={featured.cover_image} alt={featured.title} loading="eager" width="800" height="500" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6 md:p-8">
                <span className="inline-block text-xs font-semibold text-primary bg-primary/10 rounded-full px-3 py-1">{featured.category}</span>
                <h2 className="font-display font-bold text-2xl sm:text-3xl leading-tight mt-3 group-hover:text-primary transition-colors">{featured.title}</h2>
                <p className="text-muted-foreground mt-3">{featured.excerpt}</p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-4">
                  <span>{featured.author}</span><span aria-hidden="true">·</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{featured.reading_time} min read</span>
                </div>
              </div>
            </Link>
          </section>
        )}

        {/* Category nav */}
        <nav aria-label="Article categories" className="flex flex-wrap gap-2 mb-8">
          {["all", ...cats].map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${active === c ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}
            >
              {c === "all" ? "All Stories" : c}
            </button>
          ))}
        </nav>

        <div className="grid lg:grid-cols-[1fr_320px] gap-10">
          {/* Main content */}
          <div>
            {loading ? (
              <p className="text-muted-foreground py-12 text-center">Loading stories…</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-6">
                {rest.map((p) => <BlogCard key={p.slug} post={p} />)}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-8" aria-label="Sidebar">
            <Newsletter compact />
            <AdSlot placement="sidebar" />
            <section className="rounded-2xl border border-border p-5" aria-label="Popular stories">
              <h3 className="font-display font-bold text-lg mb-3">Popular Stories</h3>
              <ul className="space-y-3">
                {posts.slice(0, 4).map((p) => (
                  <li key={p.slug}>
                    <Link to={`/blog/${p.slug}`} className="text-sm font-medium hover:text-primary line-clamp-2">{p.title}</Link>
                    <span className="block text-xs text-muted-foreground mt-0.5">{p.category}</span>
                  </li>
                ))}
              </ul>
            </section>
            <AdDisclaimer />
          </aside>
        </div>

        {/* Footer banner ad */}
        <AdSlot placement="footer" className="mt-12" />
      </main>
    </Layout>
  );
}
